import { notification, Spin, Typography, Card, Button } from 'antd';
import {
	UserOutlined,
	MailOutlined,
	LoadingOutlined,
	SafetyCertificateOutlined,
	InfoCircleOutlined,
	TranslationOutlined,
	SyncOutlined,
	BuildOutlined,
	GlobalOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import LanguageSelector from '@/components/LanguageSelector/LanguageSelector';
import { applyTranslation, getLanguageName } from '@/utils/translation';
import { Form } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { setClientDashboardLanguage, updateUserData } from '@/api/user';
import AccountSettingForm from './AccountSettingForm';
import { OverViewCardForm } from '@/pages/ReceptionistDashboard/Overview/Cards/OverviewCardForm';
import AddTimeZone from '@/pages/ReceptionistDashboard/Overview/ManageNotifications/components/AddTimeZone';

const { Text, Title, Paragraph } = Typography;

const AccountSettings: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
	const [form] = Form.useForm<{ business_name?: string }>();
	const [isFieldTouchReset, setIsFieldTouchReset] = useState<boolean>(true);
	const loggedInUser = useAppSelector(getLoggedInUser);
	const [currentLanguage, setCurrentLanguage] = useState<string>('en');
	const [isApplyingTranslation, setIsApplyingTranslation] = useState<boolean>(false);

	const mutation = useMutation({
		mutationKey: ['update-client'],
		mutationFn: updateUserData,
		onSuccess: () => {
			notification.success({
				key: 'form-success',
				message: 'Account details has been updated successfully!',
				duration: 3,
			});
			setIsFieldTouchReset(false);
		},
	});

	const updateClientDashboardLanguageMutation = useMutation({
		mutationKey: ['update-client-dashboard-language'],
		mutationFn: setClientDashboardLanguage,
	});

	const handleSubmit = (values: { business_name: string }) => {
		mutation.mutate({
			clientType: 'Client',
			data: {
				businessName: values.business_name,
			},
		});
	};

	const handleLanguageChange = (language: string) => {
		setCurrentLanguage(language);
	};

	const handleApplyTranslation = async () => {
		setIsApplyingTranslation(true);

		await updateClientDashboardLanguageMutation.mutateAsync({
			language: currentLanguage,
		});

		try {
			await applyTranslation(currentLanguage);

			notification.success({
				message: 'Translation Applied',
				description: `The dashboard has been translated to ${getLanguageName(currentLanguage)}.`,
				duration: 5,
			});
		} catch (error) {
			console.error('Error applying translation:', error);

			notification.error({
				message: 'Translation Failed',
				description: 'Could not apply translation. Please try again.',
				duration: 5,
			});
		} finally {
			setIsApplyingTranslation(false);
		}
	};

	const handleResetToEnglish = async () => {
		setIsApplyingTranslation(true);

		await updateClientDashboardLanguageMutation.mutateAsync({
			language: 'en',
		});

		try {
			await applyTranslation('en');
		} catch (error) {
			console.error('Error resetting to English:', error);

			try {
				document.documentElement.lang = 'en';

				const cookies = document.cookie.split(';');
				for (let i = 0; i < cookies.length; i++) {
					const cookie = cookies[i];
					const eqPos = cookie.indexOf('=');
					const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
					if (name.includes('goog') || name.includes('GOOG')) {
						document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
					}
				}

				setTimeout(() => {
					window.location.reload();
				}, 500);
			} catch (secondError) {
				console.error('Error during aggressive reset:', secondError);
			}
		} finally {
			setIsApplyingTranslation(false);
		}
	};

	const isResellerDashboard = window.location.href.includes('reseller_portal');

	return (
		<div className="p-8 max-w-5xl mx-auto">
			<div className="mb-8">
				<div className="flex items-center justify-between">
					<div className="flex items-center">
						<div className="bg-blue-50 p-3 rounded-lg mr-4">
							<UserOutlined className="text-2xl text-blue-500" />
						</div>
						<div>
							<Title level={2} className="!mb-0">
								Account Settings
							</Title>
							<Text type="secondary">Manage your account preferences and settings</Text>
						</div>
					</div>
				</div>
			</div>

			<Spin spinning={isLoading} indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}>
				<div className="grid gap-6">
					{/* Personal Information Card */}
					<Card
						className="shadow-sm hover:shadow-md transition-shadow duration-300"
						title={
							<div className="flex items-center">
								<SafetyCertificateOutlined className="text-green-500 mr-2" />
								<span>Personal Information</span>
							</div>
						}
					>
						<div className="space-y-6">
							{/* Username Section */}
							<div className="flex items-center p-3 rounded-lg transition-colors">
								<div className="bg-gray-100 p-2 rounded-lg mr-4">
									<UserOutlined className="text-lg text-gray-600" />
								</div>
								<div className="flex-1">
									<Text type="secondary" className="block text-sm">
										Username
									</Text>
									<Text strong className="text-base">
										{loggedInUser?.username}
									</Text>
								</div>
							</div>

							{/* Email Section */}
							<div className="flex items-center p-3 rounded-lg transition-colors">
								<div className="bg-gray-100 p-2 rounded-lg mr-4">
									<MailOutlined className="text-lg text-gray-600" />
								</div>
								<div className="flex-1">
									<Text type="secondary" className="block text-sm">
										Email Address
									</Text>
									<Text strong className="text-base">
										{loggedInUser?.emailAddress}
									</Text>
								</div>
							</div>

							{/* Business Name Section */}
							<div className="flex items-start p-3 rounded-lg transition-colors">
								<div className="bg-gray-100 p-2 rounded-lg mr-4 mt-1">
									<BuildOutlined className="text-lg text-gray-600" />
								</div>
								<div className="flex-1">
									<Text type="secondary" className="block text-sm mb-2">
										Business Name
									</Text>
									<OverViewCardForm
										isFieldTouchReset={isFieldTouchReset}
										form={form}
										handleSubmit={handleSubmit}
										formItems={
											<AccountSettingForm
												initialValues={{ business_name: loggedInUser?.businessName || '' }}
											/>
										}
										initialValues={{ business_name: loggedInUser?.businessName || '' }}
									/>
								</div>
							</div>
						</div>
					</Card>

					{/* Time Zone Card */}
					<Card
						className="shadow-sm hover:shadow-md transition-shadow duration-300"
						title={
							<div className="flex items-center">
								<GlobalOutlined className="text-blue-500 mr-2" />
								<span>Time Zone Settings</span>
							</div>
						}
					>
						<div className="space-y-4">
							<Paragraph type="secondary" className="mb-6">
								Select your preferred time zone. This will be used for all date and time displays
								across the application.
							</Paragraph>

							<div className="bg-gray-50 p-4 rounded-lg w-full">
								<AddTimeZone />
							</div>

							<div className="mt-4 p-3 bg-blue-50 rounded-lg">
								<Text type="secondary" className="text-sm">
									<InfoCircleOutlined className="mr-2" />
									Your current time zone will be used for all notifications and scheduled activities
								</Text>
							</div>
						</div>
					</Card>

					{/* Language Settings Card */}
					<Card
						className="shadow-sm hover:shadow-md transition-shadow duration-300"
						title={
							<div className="flex items-center">
								<TranslationOutlined className="text-blue-500 mr-2" />
								<span>Language Settings</span>
							</div>
						}
					>
						<div className="space-y-4">
							<Paragraph type="secondary" className="mb-6">
								Select your preferred language. This will translate the content across the
								application.
							</Paragraph>

							<div className="bg-gray-50 p-4 rounded-lg">
								<div className="flex items-center p-3">
									<div className="bg-gray-100 p-2 rounded-lg mr-4">
										<TranslationOutlined className="text-lg text-gray-600" />
									</div>
									<div className="flex-1">
										<div className="custom-language-selector">
											<LanguageSelector
												showLabel={false}
												showInfo={false}
												className="w-full"
												onLanguageChange={handleLanguageChange}
												isResellerDashboard={isResellerDashboard}
											/>
										</div>
									</div>
								</div>

								<div className="mt-4 flex justify-end space-x-2">
									<Button
										type="primary"
										onClick={handleResetToEnglish}
										icon={<SyncOutlined />}
										loading={isApplyingTranslation && currentLanguage === 'en'}
										disabled={isResellerDashboard || isApplyingTranslation}
										className="flex items-center gap-2"
									>
										<span className="flex items-center">Reset to English</span>
										<img src="https://cdn.gtranslate.net/flags/svg/en-us.svg" className="w-4 h-4" />
									</Button>

									{currentLanguage !== 'en' && (
										<Button
											type="primary"
											onClick={handleApplyTranslation}
											icon={<TranslationOutlined />}
											loading={isApplyingTranslation && currentLanguage !== 'en'}
											disabled={isResellerDashboard || isApplyingTranslation}
										>
											Translate to {getLanguageName(currentLanguage)}
										</Button>
									)}
								</div>
							</div>

							<div
								className={`mt-4 p-3 ${isResellerDashboard ? 'bg-yellow-50' : 'bg-blue-50'} rounded-lg`}
							>
								<Text type={isResellerDashboard ? 'warning' : 'secondary'} className="text-sm">
									<InfoCircleOutlined className="mr-2" />
									{isResellerDashboard
										? 'Please login to this account in a separate tab to use this feature, this functionality is not available in popup view.'
										: 'Click the appropriate button to apply the translation or reset to English.'}
								</Text>
							</div>
						</div>
					</Card>
				</div>
			</Spin>
		</div>
	);
};

export default AccountSettings;
